const NOTIFICATION_TYPES = {
  NOTIFICATION_WITH_OK: 'NOTIFICATION_WITH_OK',
  NOTIFICATION_ACCEPT: 'NOTIFICATION_ACCEPT',
  NOTIFICATION_CANCELABLE: 'NOTIFICATION_CANCELABLE',
  NOTIFICATION_INFO: 'NOTIFICATION_INFO'
} as const;

type NotificationTypesType = keyof typeof NOTIFICATION_TYPES;

interface NotificationOptions {
  type: keyof typeof NOTIFICATION_TYPES,
  tone: NotificationToneType,
  message: string,
  duration?: number,
  action?: Function
}

const NOTIFICATION_TONE = {
  INFO: {
    border: "border-neutral-900",
    background: "bg-neutral-800",
  },
  ERROR: {
    border: "border-red-600",
    background: "bg-red-500",
  }
} as const;

type NotificationToneType = keyof typeof NOTIFICATION_TONE;
type Tone = typeof NOTIFICATION_TONE[NotificationToneType];

class NotificationsManager {
  notificationManagerEl: HTMLElement;
  messageEl: HTMLElement;
  cancelButtonEl: HTMLElement;
  actionButtonEl: HTMLElement;
  action: Function | undefined;

  constructor() {
    this.notificationManagerEl = document.querySelector('#notification-manager') as HTMLElement;
    this.messageEl = this.notificationManagerEl.querySelector('#notification-message') as HTMLElement;
    this.cancelButtonEl = this.notificationManagerEl.querySelector('#notification-action-cancel') as HTMLButtonElement;
    this.actionButtonEl = this.notificationManagerEl.querySelector('#notification-action-accept') as HTMLButtonElement;

    this.cancelButtonEl.addEventListener('click', () => {
      this.hide();
      this.action = undefined;
    });

    this.actionButtonEl.addEventListener('click', () => {
      if (typeof this.action === 'function') {
        this.action();
        this.hide();
      }
    });

    const params = new URLSearchParams(window.location.search)
    if (params.get('login') === 'success') {
      this.open({
        type: NOTIFICATION_TYPES.NOTIFICATION_INFO,
        tone: "INFO",
        message: "you are logged in!",
      });
    }
    if (params.get('login') === 'logout') {
      this.open({
        type: NOTIFICATION_TYPES.NOTIFICATION_INFO,
        tone: "INFO",
        message: "you are logged out.",
      });
    }
    if (params.get('login') === 'failed_login') {
      this.open({
        type: NOTIFICATION_TYPES.NOTIFICATION_INFO,
        tone: "ERROR",
        message: `we're sorry, but we don't recognize your sign in.\n
				you may want to try resetting your password.\n
				if you need additional help, please email us at contact@cletile.com\n
				`
      });
    }
    if (params.get('login') === 'already_exists') {
      this.open({
        type: NOTIFICATION_TYPES.NOTIFICATION_INFO,
        tone: "ERROR",
        message: "a customer with the exact same email address already exists.",
      });
    }
    if (params.get('login') === 'password_reset') {
      this.open({
        type: NOTIFICATION_TYPES.NOTIFICATION_INFO,
        tone: "INFO",
        message: "successfully changed password.",
      });
    }
  }

  setNotificationActions (elementIds?: string[]) {
    this.actionButtonEl.style.display = '';
    this.cancelButtonEl.style.display = '';
    elementIds?.forEach(id => {
      const el = document.getElementById(id);
      if (el) el.style.display = 'none';
    });
  }

  setNotificationTone ({ border, background }: Tone) {
    const actionsEl = this.notificationManagerEl.querySelector('#notification-actions') as HTMLElement;

    this.notificationManagerEl.classList.remove('bg-red-500', 'bg-neutral-800')
    this.notificationManagerEl.classList.add(background)

    actionsEl.classList.remove('border-red-600', 'border-neutral-900')
    actionsEl.classList.add(border)

    this.messageEl.classList.remove('border-neutral-900', 'border-red-600')
    this.messageEl.classList.add(border)

    this.cancelButtonEl.classList.remove('border-neutral-900', 'border-red-600')
    this.cancelButtonEl.classList.add(border)

    return;
  }

  setNotificationMessage (message: string) {
    const messageEl = this.notificationManagerEl.querySelector('#notification-message');
    if (messageEl) {
      messageEl.innerHTML = message;
    }
  }

  open ({ type, tone = "INFO", message, duration = 3000, action }: NotificationOptions) {
    this.setNotificationTone(NOTIFICATION_TONE[tone]);
    this.setNotificationMessage(message);
    if (this.actionButtonEl && action) {
      this.action = action;
    }
    switch (type) {
      case NOTIFICATION_TYPES.NOTIFICATION_ACCEPT:
        this.setNotificationActions(['notification-action-cancel']);
        this.show();
        break;
      case NOTIFICATION_TYPES.NOTIFICATION_CANCELABLE:
        this.setNotificationActions();
        this.show();
      break;
      case NOTIFICATION_TYPES.NOTIFICATION_INFO:
        setTimeout(() => {
          this.hide();
        }, duration);
        this.setNotificationActions(['notification-action-cancel', 'notification-action-accept']);
        this.show();
        break;
      default:
        break;
    }
  }

  hide () {
    this.notificationManagerEl.classList.add('animate-fade-out');
    this.notificationManagerEl.classList.remove('animate-slide-down-fade-in');
    this.notificationManagerEl.addEventListener('animationend', () => {
      this.notificationManagerEl.classList.add('hidden');
      this.notificationManagerEl.classList.add('animate-slide-down-fade-in');
      this.notificationManagerEl.classList.remove('animate-fade-out');
      if (this.messageEl) {
        this.messageEl.innerHTML = '';
      }
    }, { once: true });
  }

  show () {
    this.notificationManagerEl.classList.remove('hidden');
  }
}

const notificationsManager = new NotificationsManager();
window.notificationsManager = notificationsManager
